/**
 * ============================================================================
 *  File        : frontend/src/api/reports.ts
 *  Module      : api
 *  Purpose     : 最終重量 / 最終報告 唯讀資料 GET（S7/S8）+ 三方確認（formal spec §K）
 *  Author      : EF Engineering
 *  Created     : 2026-06-01
 *  Updated     : 2026-06-14
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  後端 handler（掛在 v1）：
 *    GET  /api/v1/cases/:id/final-weight              -> v12_post_award_weights（v13 案件回 404 → null）
 *    GET  /api/v1/cases/:case_id/final-report         -> v13_case_spine::get_final_report
 *                                                        （marketplace.final_report.view;snapshot+hash+confirmations）
 *    POST /api/v1/cases/:case_id/final-report/confirm -> v13_case_spine::confirm_final_report
 *                                                        （marketplace.final_report.confirm;{party}）
 *
 *  最終報告 read model（formal spec §K）：FinalReportReview 開始時後端採 snapshot + 算 blake3 hash
 *  並 upsert final_reports。snapshot 採取前（未進入 review）GET 回 404 FINAL_REPORT_NOT_FOUND →
 *  這裡轉 null（不阻擋畫面）。三方確認齊（ef+member+venue）才可結案。
 */

import { API_BASE, ApiError, mutationHeaders, requestData } from './client'

const CASES_PATH = `${API_BASE}/cases`

/** v12 service 對 v13 案件「查無紀錄」的錯誤碼（視為 null,不阻擋畫面）。 */
const V12_NOT_FOUND_CODE = 'V12_BID_EVENT_NOT_FOUND'
/** v13 最終報告「尚未採取 snapshot」的錯誤碼（視為 null,不阻擋畫面）。 */
const FINAL_REPORT_NOT_FOUND_CODE = 'FINAL_REPORT_NOT_FOUND'

/** 三方確認當事者。 */
export type FinalConfirmationParty = 'ef' | 'member' | 'venue'

/** 三方確認 1 件（= 後端 FinalConfirmationDto）。 */
export interface FinalConfirmationDto {
  party: FinalConfirmationParty
  confirmed_by_user_id: number | null
  confirmed_at: string
}

/** 最終報告 read model（= 後端 FinalReportViewDto）。 */
export interface FinalReportViewDto {
  case_id: number
  /** 報告 snapshot（自由 JSON;案件 + 品目 + P1/P2 + 最終重量 + 落札）。 */
  snapshot: unknown
  /** blake3(canonical(snapshot)) 的 64-char hex。 */
  hash: string
  status: string
  confirmations: FinalConfirmationDto[]
}

/** 將「查無紀錄(404 / 指定 code)」轉為 null;其餘錯誤照拋。 */
async function tolerateNotFound<T>(promise: Promise<T>, ...codes: string[]): Promise<T | null> {
  try {
    return await promise
  } catch (error) {
    if (
      error instanceof ApiError &&
      (error.status === 404 || (error.body != null && codes.includes(error.body.error.code)))
    ) {
      return null
    }
    throw error
  }
}

/** 取得最終重量 snapshot（v13 案件目前回 null;workflow-state 為權威狀態來源）。 */
export function getFinalWeight(caseId: number | string): Promise<unknown> {
  return tolerateNotFound(
    requestData<unknown>(`${CASES_PATH}/${caseId}/final-weight`, { method: 'GET' }),
    V12_NOT_FOUND_CODE,
  )
}

/**
 * 取得最終報告 read model（v13;snapshot + hash + confirmations）。
 * 尚未進入 FinalReportReview（未採 snapshot）時回 404 → null（不阻擋畫面）。
 */
export function getFinalReport(caseId: number | string): Promise<FinalReportViewDto | null> {
  return tolerateNotFound(
    requestData<FinalReportViewDto>(`${CASES_PATH}/${caseId}/final-report`, { method: 'GET' }),
    FINAL_REPORT_NOT_FOUND_CODE,
  )
}

/**
 * 三方確認（formal spec §K）— 記錄 actor 自方確認（後端驗 actor org 與 party 一致）。冪等。
 * party 不符 actor org → 403 FINAL_CONFIRMATION_PARTY_FORBIDDEN。
 */
export function confirmFinalReport(
  caseId: number | string,
  party: FinalConfirmationParty,
): Promise<{ case_id: number; workflow_state: string }> {
  return requestData<{ case_id: number; workflow_state: string }>(
    `${CASES_PATH}/${caseId}/final-report/confirm`,
    {
      method: 'POST',
      headers: mutationHeaders(),
      body: { party },
    },
  )
}
